import type { Currency } from '../../constants';

// Point earning (Redeem, Earn, etc.)
export interface EarnPointsRequest {
  customerId: string;
  points: number;
  activityType: string;
  pointType: string;
  orderId?: string;
  campaignId?: string;
  expiryDays?: number;
}

export interface PointTransactionResponse {
  customerId: string;
  transactionId: string;
  timestamp: string;
  pointsDelta: number;
  remainingPoints: number;
  activityType: string;
  pointType: string;
  earnDate: string;
  expireAt: string;
  creditDelta: number;
  remainingCredit: number;
  /**
   * Ready-to-render second line for the transaction row, composed by the server — `Order #12345`
   * for order-bound activity, `SkinAnalysisCompletion` for the Skin Analysis reward. The key is
   * always present; `null` means the row has no second line. Clients render it verbatim and no
   * longer compose one from `orderId`/`transactionId`.
   */
  subDescription: string | null;
  orderId?: string;
  rewardId?: string;
  campaignId?: string;
}

// Point redemption / Reward exchange
export interface RedeemPointsRequest {
  customerId: string;
  points: number;
  rewardId: string;
}

export interface RedeemPointsResponse {
  success: boolean;
  redeemedPoints: number;
}

export interface NextExpiringCredit {
  credit: number;
  expireAt: string;
}

// User point balance and tier
export interface CustomerBalanceResponse {
  customerId: string;
  availablePoints: number;
  earned: number;
  redeemed: number;
  tier: string;
  userRegistered?: string;
  /** Display currency code */
  currency: Currency;
  /** Fraction digits for display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  /** Available credit in display currency (converted from availablePoints) */
  availableCredit: number;
  /** Total credit earned in display currency (lifetime) */
  earnedCredit: number;
  /** Total credit redeemed in display currency (lifetime) */
  redeemedCredit: number;
  nextExpiring: NextExpiringCredit | null;
}

// Transaction history
export interface TransactionHistoryResponse {
  transactions: PointTransactionResponse[];
  /** Display currency code */
  currency: Currency;
  /** Fraction digits for display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  /** Base64-encoded pagination cursor */
  lastEvaluatedKey?: string;
}

// Tier update request/response
export interface UpdateTierRequest {
  customerId: string;
  newTier: string;
}

export interface UpdateCustomerTierResponse {
  customerId: string;
  tier: string;
}

// Reward creation/retrieval
export interface CreateRewardRequest {
  name: string;
  description: string;
  pointCost: number;
  status: string;
}

export interface RewardResponse {
  rewardId: string;
  name: string;
  description: string;
  pointCost: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
}

// Tier definition
export interface CreateTierDefinitionRequest {
  tier: string;
  rank: number;
  minPoints: number;
  benefits: string[];
}

export interface TierDefinitionResponse {
  tier: string;
  rank: number;
  minPoints: number;
  benefits: string[];
  createdAt: string;
  updatedAt?: string;
}

// Tier point information for earning calculation
export interface TierPointInfo {
  tier: string;
  pointMultiplier: number;
  pointsPerCurrency: number;
}

// Loyalty policy configuration
export interface LoyaltyPolicyResponse {
  currencyCode: string;
  currencyPerPoint: number;
  maxWeeklyInteractionPoint: number;
  currentWeeklyInteractionPoint: number;
  pointValueInCurrency: number;
  /** Fraction digits for the display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  maxApplicablePointsPercent: number;
  tierPointInfo?: TierPointInfo[];
  interactionPoints: {
    Browsing: number;
  };
}

/** Which base earn rate applies to the customer: 1% (BASE) or 5% inside the 60-day repurchase window. */
export const UserCreditBenefitType = {
  Base: 'BASE',
  RecentPurchase: 'RECENT_PURCHASE',
} as const;

export type UserCreditBenefitType = (typeof UserCreditBenefitType)[keyof typeof UserCreditBenefitType];

export interface UserCreditBenefit {
  type: UserCreditBenefitType;
  /** The base earn rate applied to the customer, e.g. 1 or 5. */
  creditRatePercentage: number;
}

/** Components of a product's credit preview. All amounts are in the display currency. */
export interface CreditBreakdown {
  /** Earning at the customer's 1% or 5% rate; an active campaign is already applied. */
  purchaseCredit: number;
  /** Additional earning from the product's boost. */
  bonusCredit: number;
  /** purchaseCredit + bonusCredit. */
  totalCredit: number;
}

/** GET /loyalty/products/{productId}/creditAmount */
export interface ProductCreditPreview {
  /** Display currency code */
  currency: Currency;
  /** Fraction digits for display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  userCreditBenefit: UserCreditBenefit;
  creditBreakdown: CreditBreakdown;
}

/** POST /cart/expectedCredit — aggregate credit the current cart would earn, after coupons and applied credit. */
export interface CartExpectedCredit {
  points: number;
  creditAmount: number;
  currency: Currency;
  fractionDigits: number;
}
